'use client';

import { createContext, useContext, useMemo, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { apiClient, type User } from '@/lib/api';
import { getAccessToken, getStoredUserRaw, setStoredUser } from '@/lib/authStorage';
import { useSiteSettings } from '@/lib/swr/hooks/useSiteSettings';
import { normalizeSiteTheme, normalizeSiteThemes, resolveSiteTheme, SITE_THEME_GUEST_KEY, themeVariables, type SiteThemeId } from '@/lib/siteTheme';
import { createSiteThemeWriter } from '@/lib/siteThemeWriter';

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
const ThemeContext = createContext({ theme: 'light' as SiteThemeId, status: 'idle' as SaveStatus, tokens: resolveSiteTheme('light'), select: (_theme: SiteThemeId) => {}, retry: () => {} });
export const useSiteTheme = () => useContext(ThemeContext);

function storedUser(): User | null {
  try { return getAccessToken() ? JSON.parse(getStoredUserRaw() || 'null') : null; } catch { return null; }
}
function guestTheme(): SiteThemeId {
  try { return normalizeSiteTheme(localStorage.getItem(SITE_THEME_GUEST_KEY)); } catch { return 'light'; }
}

export default function SiteThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<SiteThemeId>('light');
  const [status, setStatus] = useState<SaveStatus>('idle');
  const { settings } = useSiteSettings();
  const themeRef = useRef<SiteThemeId>('light');
  const userIdRef = useRef<number | null>(null);
  const revision = useRef(0);
  const writer = useRef<ReturnType<typeof createSiteThemeWriter> | null>(null);
  const initialized = useRef(false);
  const tokens = useMemo(() => resolveSiteTheme(theme, normalizeSiteThemes(settings?.site_themes).themes[theme].overrides), [theme, settings?.site_themes]);

  useLayoutEffect(() => {
    const sync = () => {
      const user = storedUser();
      const id = user?.id ?? null;
      if (initialized.current && id === userIdRef.current) return;
      initialized.current = true;
      revision.current++;
      writer.current?.dispose();
      userIdRef.current = id;
      const next = user ? normalizeSiteTheme(user.preferences?.site_theme) : guestTheme();
      themeRef.current = next;
      setTheme(next);
      setStatus('idle');
      writer.current = id === null ? null : createSiteThemeWriter(
        (value) => apiClient.updatePreferredSiteTheme(value), setStatus,
      );
      if (id !== null) {
        const snapshot = revision.current;
        void apiClient.getCurrentUser().then((fresh) => {
          if (revision.current !== snapshot || fresh.id !== userIdRef.current) return;
          const value = normalizeSiteTheme(fresh.preferences?.site_theme);
          themeRef.current = value;
          setTheme(value);
          const current = storedUser();
          if (current?.id === id) setStoredUser({ ...current, preferences: { ...current.preferences, site_theme: value } });
        }).catch(() => { /* При недоступном API остаётся сохранённый локальный выбор. */ });
      }
    };
    sync();
    const storage = (event: StorageEvent) => {
      if (event.key === SITE_THEME_GUEST_KEY && userIdRef.current === null) {
        themeRef.current = guestTheme(); setTheme(themeRef.current);
      } else if (['access', 'user', null].includes(event.key)) {
        const user = storedUser();
        if (event.key === 'user' && user && user.id === userIdRef.current) {
          revision.current++;
          themeRef.current = normalizeSiteTheme(user.preferences?.site_theme);
          setTheme(themeRef.current);
        } else sync();
      }
    };
    window.addEventListener('lisad_auth_changed', sync);
    window.addEventListener('storage', storage);
    return () => {
      revision.current++;
      initialized.current = false;
      writer.current?.dispose();
      window.removeEventListener('lisad_auth_changed', sync);
      window.removeEventListener('storage', storage);
    };
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.siteTheme = theme;
    root.classList.toggle('dark', theme !== 'light');
    for (const [key, value] of Object.entries(themeVariables(tokens))) root.style.setProperty(key, value);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', tokens.bg);
  }, [theme, tokens]);

  const select = (value: SiteThemeId) => {
    revision.current++;
    themeRef.current = value;
    setTheme(value);
    const user = storedUser();
    if (user && user.id === userIdRef.current) {
      setStoredUser({ ...user, preferences: { ...user.preferences, site_theme: value } });
      writer.current?.select(value);
    } else {
      try { localStorage.setItem(SITE_THEME_GUEST_KEY, value); } catch { /* Тема работает и без Storage. */ }
      setStatus('idle');
    }
  };
  // Обновления настроек SWR не перемонтируют дерево и не сбрасывают формы.
  return <ThemeContext.Provider value={{ theme, status, tokens, select, retry: () => writer.current?.select(themeRef.current) }}>{children}</ThemeContext.Provider>;
}
